import { useId, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { ArrowUpRight, Link2, MessagesSquare } from "lucide-react";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { TabHeader } from "~/pages/manifold-nextgen/components/TabHeader";
import { useCanEditAgent } from "~/pages/manifold-nextgen/hooks/useCanEditAgent";
import { nextgenSharedConversationPath } from "~/pages/manifold-nextgen/paths";
import {
  Agent,
  AgentFeaturedConversation,
  AgentFeaturedConversationInput,
  FEATURED_CONVERSATION_LABEL_MAX_LENGTH,
  FEATURED_CONVERSATION_URL_MAX_LENGTH,
  MAX_FEATURED_CONVERSATIONS,
} from "~/services/agents";
import {
  AgentSortableListTab,
  EditFormFooter,
  type EditFormRenderProps,
} from "./AgentSortableListTab";

const toInput = (item: AgentFeaturedConversation): AgentFeaturedConversationInput => ({
  label: item.label,
  url: item.url,
});

const isValidUrl = (value: string): boolean => {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
};

const extractShareToken = (url: string): string | null => {
  try {
    return new URL(url).pathname.split("/").at(-1) || null;
  } catch {
    return null;
  }
};

/** Launchable, read-only gallery shown to users who can't manage the agent. */
const FeaturedSessionsGallery = ({ agent }: { agent: Agent }) => {
  const navigate = useNavigate();
  const sessions = agent.featured_conversations
    .map((fc) => ({ fc, token: extractShareToken(fc.url) }))
    .filter((s): s is { fc: AgentFeaturedConversation; token: string } => Boolean(s.token));

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4">
      <TabHeader title="Featured Sessions" context="Example sessions curated for this agent" />
      {sessions.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
          No featured sessions yet.
        </div>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2" role="list">
          {sessions.map(({ fc, token }) => (
            <li key={fc.id}>
              <button
                type="button"
                onClick={() =>
                  navigate(nextgenSharedConversationPath(token), {
                    state: { agentDisplayName: agent.display_name },
                  })
                }
                className="group flex w-full items-center gap-3 rounded-lg border border-border bg-card p-4 text-left transition-colors hover:border-primary/40 hover:bg-accent/40"
              >
                <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <MessagesSquare className="size-4" />
                </span>
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm font-medium text-foreground">{fc.label}</span>
                  <span className="text-xs text-muted-foreground">Open featured session</span>
                </span>
                <ArrowUpRight className="size-4 shrink-0 text-muted-foreground/50 transition-colors group-hover:text-primary" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const FeaturedConversationView = ({ item }: { item: AgentFeaturedConversation }) => (
  <div className="flex min-w-0 flex-col gap-0.5">
    <p className="truncate text-sm font-medium text-foreground">{item.label}</p>
    <div className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
      <Link2 className="size-3 shrink-0" />
      <span className="truncate">{item.url}</span>
    </div>
  </div>
);

const FeaturedConversationEditForm = ({
  initial,
  disabled,
  onSave,
  onCancel,
}: EditFormRenderProps<AgentFeaturedConversationInput>) => {
  const labelId = useId();
  const urlId = useId();
  const [label, setLabel] = useState(initial.label);
  const [url, setUrl] = useState(initial.url);

  const trimmedLabel = label.trim();
  const trimmedUrl = url.trim();
  const labelValid =
    trimmedLabel.length > 0 && trimmedLabel.length <= FEATURED_CONVERSATION_LABEL_MAX_LENGTH;
  const urlValid =
    trimmedUrl.length > 0 &&
    trimmedUrl.length <= FEATURED_CONVERSATION_URL_MAX_LENGTH &&
    isValidUrl(trimmedUrl);
  const canSave = labelValid && urlValid && !disabled;

  const handleSubmit = () => {
    if (!canSave) return;
    onSave({ label: trimmedLabel, url: trimmedUrl });
  };

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={labelId}>Label</Label>
        <Input
          id={labelId}
          autoFocus
          placeholder="e.g. ERBB2 amplification cohort"
          maxLength={FEATURED_CONVERSATION_LABEL_MAX_LENGTH}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          disabled={disabled}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={urlId}>Shared conversation URL</Label>
        <Input
          id={urlId}
          placeholder="https://manifold.sc.manifold.ai/shared/conversations/…"
          maxLength={FEATURED_CONVERSATION_URL_MAX_LENGTH}
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          disabled={disabled}
        />
      </div>
      <EditFormFooter
        counterCurrent={label.length}
        counterMax={FEATURED_CONVERSATION_LABEL_MAX_LENGTH}
        canSave={canSave}
        disabled={disabled}
        onCancel={onCancel}
        onSave={handleSubmit}
      />
    </>
  );
};

export const AgentFeaturedConversationsTab = () => {
  const agent = useOutletContext<Agent>();
  const canEdit = useCanEditAgent(agent);

  if (!canEdit) {
    return <FeaturedSessionsGallery agent={agent} />;
  }

  return (
    <AgentSortableListTab<AgentFeaturedConversation, AgentFeaturedConversationInput>
      title="Featured Sessions"
      context="Showcased on this agent for anyone to explore"
      description={
        <>
          Pin up to {MAX_FEATURED_CONVERSATIONS} example sessions that anyone viewing this agent can
          open and explore. Drag to reorder. URLs must point to{" "}
          <code className="rounded bg-muted px-1 py-0.5 text-xs">/shared/conversations/…</code>.
        </>
      }
      agent={agent}
      field="featured_conversations"
      items={agent.featured_conversations}
      maxItems={MAX_FEATURED_CONVERSATIONS}
      canEdit={canEdit}
      toInput={toInput}
      emptyDraft={{ label: "", url: "" }}
      renderView={(item) => <FeaturedConversationView item={item} />}
      renderEditForm={(props) => <FeaturedConversationEditForm {...props} />}
      addLabel="Add featured session"
      emptyMessage="No featured sessions yet."
      errorMessage="Failed to save featured sessions"
    />
  );
};
